// Footsteps + the walker's whistle.
//
// Steps play real samples from public/sfx/steps (stone1-4), picked at random
// with a little pitch variation so a walk never loops audibly. If the samples
// haven't loaded (or can't), it falls back to a synthesised thud so walking is
// never silent.

import { getCtx, unlockAudio, loadBuffer } from './context'

export { unlockAudio }

const STEP_FILES = [
  '/sfx/steps/stone1.wav',
  '/sfx/steps/stone2.wav',
  '/sfx/steps/stone3.wav',
  '/sfx/steps/stone4.wav',
]

let stepBuffers = null
let loading = false
let lastStepIdx = -1

/**
 * Decode the step samples. Safe to call repeatedly: it no-ops once loaded and
 * retries if a previous attempt failed. Decoding does NOT need a user gesture
 * (only playback does), so this is called on mount as well as on first click.
 * Logs the outcome, since a silent failure just sounds like the fallback.
 */
export function loadFootsteps() {
  if (stepBuffers || loading) return
  loading = true
  Promise.all(STEP_FILES.map((f) => loadBuffer(f)))
    .then((bufs) => {
      const ok = bufs.filter(Boolean)
      stepBuffers = ok.length ? ok : null
      if (ok.length === STEP_FILES.length) {
        console.log(`[audio] footstep samples loaded (${ok.length})`)
      } else {
        console.warn(`[audio] only ${ok.length}/${STEP_FILES.length} footstep samples decoded; using the synth for the rest`)
      }
    })
    .catch((err) => {
      stepBuffers = null
      console.warn(`[audio] footstep samples failed to load (${err?.message || err}); falling back to the synth`)
    })
    .finally(() => { loading = false })
}

/** True once the real samples are in memory (diagnostics / tests). */
export function footstepsReady() {
  return !!(stepBuffers && stepBuffers.length)
}

// --- fallback synth (used until the samples land, or if they fail) ---
const SYNTH_DUR = 0.13
let synthPool = null
let synthIdx = 0

function getSynthPool(c) {
  if (synthPool) return synthPool
  synthPool = []
  const frames = Math.max(1, Math.floor(c.sampleRate * SYNTH_DUR))
  for (let n = 0; n < 4; n++) {
    const buffer = c.createBuffer(1, frames, c.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < frames; i++) {
      const t = i / frames
      const env = (1 - t) * (1 - t)
      data[i] = (Math.random() * 2 - 1) * env
    }
    synthPool.push(buffer)
  }
  return synthPool
}

// One footstep. vol 0..1. Fails silently if audio is unavailable/blocked.
// Was 0.35; Sam asked for 40% down, and the garden beds make loud steps
// stand out more than they used to.
export function playFootstep(vol = 0.21) {
  const c = getCtx()
  if (!c || c.state !== 'running') return
  const now = c.currentTime

  let buffer
  let lowpass = false
  if (!stepBuffers && !loading) loadFootsteps()   // self-heal a failed/late load
  if (stepBuffers && stepBuffers.length) {
    // Avoid repeating the same sample twice in a row.
    let i = Math.floor(Math.random() * stepBuffers.length)
    if (stepBuffers.length > 1 && i === lastStepIdx) i = (i + 1) % stepBuffers.length
    lastStepIdx = i
    buffer = stepBuffers[i]
  } else {
    const pool = getSynthPool(c)
    buffer = pool[synthIdx]
    synthIdx = (synthIdx + 1) % pool.length
    lowpass = true
  }

  const src = c.createBufferSource()
  src.buffer = buffer
  src.playbackRate.value = 0.92 + Math.random() * 0.16 // per-step variation

  const gain = c.createGain()
  gain.gain.value = Math.max(0, Math.min(1, vol))

  if (lowpass) {
    const lp = c.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 480 + Math.random() * 160
    src.connect(lp)
    lp.connect(gain)
  } else {
    src.connect(gain)
  }
  gain.connect(c.destination)
  src.start(now)
}

// A soft two-note whistle: the walker call. Fails silent.
export function playWhistle(vol = 0.16) {
  const c = getCtx()
  if (!c || c.state !== 'running') return
  const t0 = c.currentTime
  const gain = c.createGain()
  gain.gain.value = 0
  gain.connect(c.destination)
  const osc = c.createOscillator()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(920, t0)
  osc.frequency.exponentialRampToValueAtTime(1380, t0 + 0.09)
  osc.frequency.setValueAtTime(1180, t0 + 0.16)
  osc.frequency.exponentialRampToValueAtTime(880, t0 + 0.3)
  gain.gain.linearRampToValueAtTime(vol, t0 + 0.02)
  gain.gain.setValueAtTime(vol, t0 + 0.12)
  gain.gain.linearRampToValueAtTime(0.0001, t0 + 0.32)
  osc.connect(gain)
  osc.start(t0)
  osc.stop(t0 + 0.34)
}
